import { useState, useEffect } from 'react';
import axios from 'axios';
import { AUTH_ENDPOINTS, SUBSCRIPTION_ENDPOINTS } from '../constants/api';
import type { UserRegistrationDetails, AgencyRegistrationDetails, SeatSelectionDetails } from '../types/Registration';

type PriceResponse = {
  base_price: number,
  ppu: number
}

interface UseAgencyRegistrationResult {
  registerAgency: (
    userDetails: UserRegistrationDetails,
    agencyDetails: AgencyRegistrationDetails
  ) => Promise<{ success: boolean; token?: string; error?: string }>;
  prices: PriceResponse | null;
  loadingPrices: boolean;
  pricesError: any; 
  isLoading: boolean;
  error: string | null;
}

// Hook for agency + user registration (full signup)
export const useAgencyRegistration = (): UseAgencyRegistrationResult => {
  const [prices, setPrices] = useState<PriceResponse | null>(null);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [pricesError, setPricesError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registerAgency = async (
    userDetails: UserRegistrationDetails,
    agencyDetails: AgencyRegistrationDetails
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      // Build payload explicitly to ensure keys match backend expectations
      const payload = {
        // USER FIELDS - Core Identity
        surname: userDetails.surname,
        fullNames: userDetails.fullNames,
        nickname: userDetails.nickname,
        email: userDetails.email,
        password: userDetails.password,
        idNumber: userDetails.idNumber,
        identificationType: userDetails.identificationType,
        dateOfBirth: userDetails.dateOfBirth ? new Date(userDetails.dateOfBirth).toISOString() : undefined,
        gender: userDetails.gender,

        // USER FIELDS - Contact
        mobileNumber: userDetails.mobileNumber,
        homeTelephone: userDetails.homeTelephone,

        // USER FIELDS - Physical Address
        unitNumber: userDetails.unitNumber,
        complex: userDetails.complex,
        streetNumber: userDetails.streetNumber,
        streetName: userDetails.streetName,
        suburb: userDetails.suburb,
        city: userDetails.city,
        postalCode: userDetails.postalCode,

        // USER FIELDS - Postal Address (send new structured postal fields)
        postalAddressSameAsResidential: userDetails.postalAddressSameAsResidential,
        postalUnitNumber: userDetails.postalUnitNumber,
        postalComplex: userDetails.postalComplex,
        postalStreetNumber: userDetails.postalStreetNumber,
        postalStreetName: userDetails.postalStreetName,
        postalSuburb: userDetails.postalSuburb,
        postalCity: userDetails.postalCity,
        postalPostalCode: userDetails.postalPostalCode,

        // AGENCY FIELDS
        companyName: agencyDetails.companyName,
        tradingAsName: agencyDetails.tradingAsName,
        companyRegistrationNumber: agencyDetails.companyRegistrationNumber,
        agencyUnitNumber: agencyDetails.unitNumber,
        agencyComplexName: agencyDetails.complexName,
        agencyStreetNumber: agencyDetails.streetNumber,
        agencyStreetName: agencyDetails.streetName,
        agencySuburbName: agencyDetails.suburbName,
        agencyCity: agencyDetails.city,
        agencyPostalCode: agencyDetails.postalCode,

        // AGREEMENT FIELDS
        agreedTerms: userDetails.agreedTerms,
        agreedPOPIA: userDetails.agreedPOPIA,
      } as any;

      // Basic client-side validation to catch issues early
      if (!payload.email || !payload.password) {
        const msg = 'Email and password are required.';
        setError(msg);
        return { success: false, error: msg };
      }

      if (!payload.idNumber) {
        const msg = 'ID number is required.';
        setError(msg);
        return { success: false, error: msg };
      }

      // Validate company registration number format client-side to match server expectation
      if (payload.companyRegistrationNumber && !/^[0-9]{4}\/\d{6}\/\d{2}$/.test(payload.companyRegistrationNumber)) {
        const msg = 'Invalid company registration number format. Expected xxxx/xxxxxx/xx';
        setError(msg);
        return { success: false, error: msg };
      }

      const response = await axios.post(AUTH_ENDPOINTS.AGENCY_REGISTER, payload);

      // token is null when email confirmation is required — registration still succeeded
      if (response.status === 201) {
        return { success: true };
      } else {
        const errorMessage = response.data.message || 'Agency registration failed. Please try again.';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Agency registration failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadPrices = async () => {
      try {
        const response = await axios.get(SUBSCRIPTION_ENDPOINTS.PRICES, {});
        setPrices(response.data);
      } catch (err) {
        setPrices(null);
        setPricesError("Unable to retrieve pricing information.");
      } finally {
        setLoadingPrices(false);
      }
    };

    loadPrices();
  }, []);

  return {
    registerAgency,
    prices,
    loadingPrices,
    pricesError,
    isLoading,
    error
  };
};